// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { getReferenceString } from '@medplum/core';
import type { Appointment, ChargeItem, DocumentReference, Encounter } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { getTelehealthInfo } from '@polarissleep/scheduling';
import { useCallback, useEffect, useState } from 'react';
import { getAppointmentTypeDisplay, getProviderFromAppointment } from '../utils/appointment-formatting';

/** Enriched appointment with related FHIR data */
export interface AppointmentDetails {
  appointment: Appointment;
  encounter: Encounter | undefined;
  copay: number | undefined;
  paymentStatus: 'paid' | 'pending' | 'n/a';
  providerName: string;
  providerId: string;
  appointmentType: string;
  /** Zoom join URL for virtual appointments (from telehealth extension) */
  joinUrl: string | undefined;
  documents: { title: string; id: string }[];
}

/**
 * Enriches a single Appointment with Encounter, ChargeItem, and DocumentReference data.
 * @param medplum - The Medplum client instance
 * @param appointment - The FHIR Appointment resource to enrich
 * @returns Enriched appointment details with copay, payment status, and documents
 */
async function enrichAppointment(
  medplum: ReturnType<typeof useMedplum>,
  appointment: Appointment
): Promise<AppointmentDetails> {
  const provider = getProviderFromAppointment(appointment);
  const appointmentType = getAppointmentTypeDisplay(appointment);
  const telehealthInfo = getTelehealthInfo(appointment);

  // Base result with no enrichment
  const base: AppointmentDetails = {
    appointment,
    encounter: undefined,
    copay: undefined,
    paymentStatus: 'n/a',
    providerName: provider.name,
    providerId: provider.id,
    appointmentType,
    joinUrl: telehealthInfo?.joinUrl,
    documents: [],
  };

  if (!appointment.id) {
    return base;
  }

  // Find the Encounter linked to this Appointment
  const encounters = await medplum.searchResources('Encounter', {
    appointment: getReferenceString(appointment),
    _count: '1',
  });

  const encounter = encounters[0];
  if (!encounter?.id) {
    return base;
  }

  base.encounter = encounter;

  // Fetch ChargeItems and DocumentReferences in parallel — use individual
  // try/catch so a 403 on one resource doesn't hide the entire appointment
  const [chargeItems, documentRefs] = await Promise.all([
    medplum
      .searchResources('ChargeItem', {
        context: getReferenceString(encounter),
        _count: '10',
      })
      .catch(() => [] as ChargeItem[]),
    medplum
      .searchResources('DocumentReference', {
        encounter: getReferenceString(encounter),
        _count: '10',
      })
      .catch(() => [] as DocumentReference[]),
  ]);

  // Compute copay from ChargeItems
  if (chargeItems.length > 0) {
    const totalCopay = chargeItems.reduce((sum: number, item: ChargeItem) => sum + (item.priceOverride?.value ?? 0), 0);
    base.copay = totalCopay;

    // Determine payment status based on ChargeItem status
    const allBilled = chargeItems.every((item: ChargeItem) => item.status === 'billed');
    base.paymentStatus = allBilled ? 'paid' : 'pending';
  }

  // Collect DocumentReference info
  base.documents = documentRefs
    .filter((doc: DocumentReference) => doc.id)
    .map((doc: DocumentReference) => ({
      title: doc.description ?? doc.type?.text ?? doc.type?.coding?.[0]?.display ?? 'Document',
      id: doc.id as string,
    }));

  return base;
}

/**
 * Hook that enriches an array of Appointment resources with Encounter,
 * ChargeItem (copay/payment), and DocumentReference (linked documents) data.
 * @param appointments - Raw FHIR Appointment resources
 * @returns Enriched appointment details, loading state, and error
 */
export function useAppointmentDetails(appointments: Appointment[]): {
  details: AppointmentDetails[];
  loading: boolean;
  error: Error | null;
} {
  const medplum = useMedplum();
  const [details, setDetails] = useState<AppointmentDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const _appointmentIds = appointments.map((a) => a.id).join(',');

  const fetchDetails = useCallback(async () => {
    if (appointments.length === 0) {
      setDetails([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const enriched = await Promise.all(appointments.map((appt) => enrichAppointment(medplum, appt)));
      setDetails(enriched);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to enrich appointments'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, appointments.map, appointments.length]);

  useEffect(() => {
    fetchDetails().catch(console.error);
  }, [fetchDetails]);

  return { details, loading, error };
}
